<template>
  <div
    class="bg-white rounded-lg shadow-[6px_4px_0_0_rgba(0,0,0,1)] border-2 border-black mt-4 sm:mt-6 overflow-hidden"
  >
    <div class="bg-purple-50 p-4 sm:p-6 rounded-t-lg border-b-2 border-gray-200">
      <div class="flex items-center gap-2 sm:gap-3 mb-1">
        <div
          class="w-6 h-6 sm:w-8 sm:h-8 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0"
        >
          <svg
            class="w-4 h-4 sm:w-5 sm:h-5 text-purple-600"
            fill="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              d="M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M12,4A8,8 0 0,1 20,12A8,8 0 0,1 12,20A8,8 0 0,1 4,12A8,8 0 0,1 12,4M11,6V14H13V6H11M11,16V18H13V16H11Z"
            />
          </svg>
        </div>
        <h2 class="text-base sm:text-xl font-semibold text-purple-900">Test History</h2>
      </div>
      <p class="text-xs sm:text-sm text-purple-700 ml-8 sm:ml-11">
        Your completed pre-tests and post-tests with scores
      </p>
    </div>
    <div class="p-3 sm:p-6">
      <!-- Loading State -->
      <div v-if="isLoading" class="text-center py-12">
        <div
          class="mx-auto mb-4 w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center"
        >
          <svg
            class="animate-spin h-8 w-8 text-purple-600"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
            ></circle>
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
        </div>
        <h3 class="text-lg font-medium text-gray-900 mb-2">Loading Tests...</h3>
        <p class="text-gray-500">Please wait while we fetch your test history.</p>
      </div>

      <!-- Not Authenticated State -->
      <div v-else-if="!currentUserId" class="text-center py-12">
        <div
          class="mx-auto mb-4 w-16 h-16 bg-red-100 rounded-full flex items-center justify-center"
        >
          <svg class="w-8 h-8 text-red-600" fill="currentColor" viewBox="0 0 24 24">
            <path
              d="M12,2C6.48,2 2,6.48 2,12C2,17.52 6.48,22 12,22C17.52,22 22,17.52 22,12C22,6.48 17.52,2 12,2M12,20C7.59,20 4,16.41 4,12C4,7.59 7.59,4 12,4C16.41,4 20,7.59 20,12C20,16.41 16.41,20 12,20M12,11A1,1 0 0,0 11,12A1,1 0 0,0 12,13A1,1 0 0,0 13,12A1,1 0 0,0 12,11M12.5,7.5A0.5,0.5 0 0,0 12,8A0.5,0.5 0 0,0 12.5,8.5A0.5,0.5 0 0,0 13,8A0.5,0.5 0 0,0 12.5,7.5Z"
            />
          </svg>
        </div>
        <h3 class="text-lg font-medium text-gray-900 mb-2">Authentication Required</h3>
        <p class="text-gray-500 mb-4 max-w-md mx-auto">Please log in to view your test history.</p>
      </div>

      <!-- Error State -->
      <div v-else-if="hasError" class="text-center py-12">
        <div
          class="mx-auto mb-4 w-16 h-16 bg-red-100 rounded-full flex items-center justify-center"
        >
          <svg class="w-8 h-8 text-red-600" fill="currentColor" viewBox="0 0 24 24">
            <path
              d="M12,2C6.48,2 2,6.48 2,12C2,17.52 6.48,22 12,22C17.52,22 22,17.52 22,12C22,6.48 17.52,2 12,2M12,20C7.59,20 4,16.41 4,12C4,7.59 7.59,4 12,4C16.41,4 20,7.59 20,12C20,16.41 16.41,20 12,20M13,17H11V15H13M13,13H11V7H13"
            />
          </svg>
        </div>
        <h3 class="text-lg font-medium text-gray-900 mb-2">Error Loading Tests</h3>
        <p class="text-gray-500 mb-4 max-w-md mx-auto">{{ errorMessage }}</p>
        <button
          @click="retryLoading"
          class="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
        >
          Try Again
        </button>
      </div>

      <!-- Empty State -->
      <div v-else-if="allTests.length === 0" class="text-center py-12">
        <div
          class="mx-auto mb-4 w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center"
        >
          <svg class="w-8 h-8 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
            <path
              d="M19,3H14.82C14.4,1.84 13.3,1 12,1C10.7,1 9.6,1.84 9.18,3H5A2,2 0 0,0 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5A2,2 0 0,0 19,3M12,3A1,1 0 0,1 13,4A1,1 0 0,1 12,5A1,1 0 0,1 11,4A1,1 0 0,1 12,3M7,7H17V5H19V19H5V5H7V7M12,17L7,12L8.41,10.58L12,14.17L15.59,10.58L17,12L12,17Z"
            />
          </svg>
        </div>
        <h3 class="text-lg font-medium text-gray-900 mb-2">No Tests Yet</h3>
        <p class="text-gray-500 mb-4 max-w-md mx-auto">
          Complete tutoring sessions to start taking pre-tests and post-tests.
        </p>
      </div>

      <!-- Tests Grid (2 columns: Pre-test and Post-test side by side) -->
      <div v-else class="w-full overflow-hidden">
        <div class="max-h-[500px] overflow-y-auto pr-1 sm:pr-2 mb-4 sm:mb-6">
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
            <div
              v-for="test in displayedPostTests"
              :key="test.id"
              class="group bg-gradient-to-br from-white to-gray-50 border-2 rounded-xl p-4 sm:p-5 hover:shadow-xl transition-all duration-300 cursor-pointer transform hover:-translate-y-1"
              :class="
                test.testType === 'pre'
                  ? 'border-blue-200 hover:border-blue-400'
                  : 'border-purple-200 hover:border-purple-400'
              "
              @click="handleView(test)"
            >
              <!-- Header with Type Badge -->
              <div class="flex items-center justify-between mb-3">
                <div
                  class="px-3 py-1 rounded-full text-xs font-bold"
                  :class="
                    test.testType === 'pre'
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-purple-100 text-purple-700'
                  "
                >
                  {{ test.testType === "pre" ? "PRE-TEST" : "POST-TEST" }}
                </div>
                <!-- Progress Badge (only for post-tests) -->
                <span
                  v-if="test.testType === 'post' && test.progressMessage"
                  class="px-2 py-1 text-[10px] sm:text-xs font-semibold rounded-full whitespace-nowrap"
                  :class="test.progressMessageClass"
                >
                  {{ test.progressMessage }}
                </span>
              </div>

              <!-- Topic/Subject -->
              <h3 class="text-base sm:text-lg font-bold text-gray-900 mb-3 line-clamp-2">
                {{ test.topic }}
              </h3>

              <!-- Score Badge - Prominent -->
              <div class="flex items-center justify-center mb-4">
                <div
                  class="w-20 h-20 sm:w-24 sm:h-24 rounded-full border-4 flex items-center justify-center transform group-hover:scale-110 transition-transform duration-300"
                  :class="
                    test.testType === 'pre'
                      ? 'bg-blue-50 border-blue-300'
                      : 'bg-purple-50 border-purple-300'
                  "
                >
                  <div class="text-center">
                    <div
                      class="text-2xl sm:text-3xl font-bold"
                      :class="test.testType === 'pre' ? 'text-blue-600' : 'text-purple-600'"
                    >
                      {{ test.score }}%
                    </div>
                  </div>
                </div>
              </div>

              <!-- Score Details -->
              <div class="space-y-2 mb-4">
                <div class="flex items-center justify-center gap-2 text-sm text-gray-600">
                  <svg
                    class="w-4 h-4 text-gray-400 flex-shrink-0"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      d="M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M12,20C7.59,20 4,16.41 4,12C4,7.59 7.59,4 12,4C16.41,4 20,7.59 20,12C20,16.41 16.41,20 12,20M16.59,7.58L10,14.17L7.41,11.59L6,13L10,17L18,9L16.59,7.58Z"
                    />
                  </svg>
                  <span class="font-semibold text-gray-900"
                    >{{ test.correctAnswers }}/{{ test.totalQuestions }}</span
                  >
                  <span class="text-gray-500">correct</span>
                </div>

                <!-- Date -->
                <div class="flex items-center justify-center gap-2 text-xs text-gray-500">
                  <svg
                    class="w-4 h-4 text-gray-400 flex-shrink-0"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      d="M19,19H5V8H19M16,1V3H8V1H6V3H5C3.89,3 3,3.89 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5C21,3.89 20.1,3 19,3H18V1M17,12H12V17H17V12Z"
                    />
                  </svg>
                  <span>{{ formatDate(test.date_completed) }}</span>
                </div>
              </div>

              <!-- View Details Button -->
              <button
                @click.stop="handleView(test)"
                class="w-full py-2.5 text-sm font-semibold rounded-lg transition-all duration-200 transform group-hover:scale-105"
                :class="
                  test.testType === 'pre'
                    ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-lg shadow-blue-200'
                    : 'bg-purple-600 text-white hover:bg-purple-700 shadow-lg shadow-purple-200'
                "
              >
                View Details →
              </button>
            </div>
          </div>
        </div>

        <!-- Pagination (max 5 items per page) -->
        <div
          v-if="allTests.length > 0"
          class="flex flex-col sm:flex-row items-center justify-between gap-2 sm:gap-0 bg-gray-50 rounded-lg p-2 sm:p-3 border border-gray-200"
        >
          <div class="text-xs sm:text-sm text-gray-600 text-center sm:text-left">
            <span class="inline-block"
              >Showing <span class="font-semibold">{{ startIndex + 1 }}-{{ endIndex }}</span></span
            >
            <span class="inline-block ml-1"
              >of <span class="font-semibold">{{ allTests.length }}</span> tests</span
            >
          </div>
          <div class="flex items-center space-x-1 sm:space-x-2">
            <button
              @click="previousPage"
              :disabled="currentPage === 1"
              :class="[
                'px-2 sm:px-3 py-1 sm:py-1.5 rounded border text-[10px] sm:text-sm font-medium transition-colors',
                currentPage === 1
                  ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50',
              ]"
            >
              <span class="hidden xs:inline">Previous</span>
              <span class="inline xs:hidden">Prev</span>
            </button>
            <div
              class="px-1.5 sm:px-3 py-1 sm:py-1.5 text-[10px] sm:text-sm font-medium text-gray-700 whitespace-nowrap"
            >
              <span class="hidden xs:inline">Page {{ currentPage }} of {{ totalPages }}</span>
              <span class="inline xs:hidden">{{ currentPage }}/{{ totalPages }}</span>
            </div>
            <button
              @click="nextPage"
              :disabled="currentPage === totalPages"
              :class="[
                'px-2 sm:px-3 py-1 sm:py-1.5 rounded border text-[10px] sm:text-sm font-medium transition-colors',
                currentPage === totalPages
                  ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50',
              ]"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- ✅ NEW: Post-Test Review Modal -->
    <PostTestReviewModal
      :is-open="isReviewModalOpen"
      :test-id="selectedTestId"
      :test-type="selectedTestDetails?.testType || 'post'"
      :test-details="selectedTestDetails"
      @close="closeReviewModal"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from "vue";
import { auth, db, collection, query, where, onSnapshot, orderBy } from "@/firebase";
import { onAuthStateChanged, type User } from "firebase/auth";
import type { QuerySnapshot, DocumentData } from "firebase/firestore";
import { logDebug, logInfo, logWarn, logError, logSuccess } from "@/utils/secureLogger";
import type { PostTestRecord } from "@/services/postTestService";
import PostTestReviewModal from "./PostTestReviewModal.vue";

// PreTest interface - knowledge-based assessment
interface PreTestRecord {
  sessionId: string;
  userId: string;
  subject: string;
  topic: string;
  date_completed: any; // ✅ FIX: Use date_completed to match Firebase field name (not completedAt)
  score: number; // Number of correct answers
  totalQuestions: number;
  percentage: number; // Score as percentage (0-100)
  questions: any[];
  language: string;
  sources: string[];
  pretest_id?: string; // Firestore document ID
  studentNotes?: string; // Student notes field
}

interface PostTestDisplay {
  id: string;
  topic: string;
  subject: string;
  score: number;
  correctAnswers: number;
  totalQuestions: number;
  date_completed: Date | string;
  session_id: string;
  testType: "pre" | "post";
  preTestScore?: number; // Pre-test score for comparison
  progressMessage?: string; // Encouraging message
  progressMessageClass?: string; // CSS class for progress badge
  studentNotes?: string; // Student notes from preTest
}

// Reactive data
const postTests = ref<PostTestDisplay[]>([]);
const preTests = ref<PostTestDisplay[]>([]); // Pre-tests for display
const preTestsRaw = ref<PreTestRecord[]>([]); // Raw pre-test data for matching
const allTests = computed(() => {
  // Combine and sort all tests by date (most recent first)
  const combined = [...postTests.value, ...preTests.value];
  return combined.sort((a, b) => {
    const aTime =
      a.date_completed instanceof Date
        ? a.date_completed.getTime()
        : new Date(a.date_completed).getTime();
    const bTime =
      b.date_completed instanceof Date
        ? b.date_completed.getTime()
        : new Date(b.date_completed).getTime();
    return bTime - aTime;
  });
});

const isLoading = ref(true);
const currentUserId = ref<string | null>(null);
const hasError = ref(false);
const errorMessage = ref("");
const currentPage = ref(1);

// ✅ NEW: Modal state
const isReviewModalOpen = ref(false);
const selectedTestId = ref<string | null>(null);
const selectedTestDetails = ref<{
  topic: string;
  subject: string;
  score: number;
  passed: boolean;
  date_completed: Date | string;
  testType: "pre" | "post"; // ✅ Add test type
} | null>(null);

let unsubscribeFirestorePostTests: (() => void) | null = null;
let unsubscribeFirestorePreTests: (() => void) | null = null;
let unsubscribeAuth: (() => void) | null = null;

const itemsPerPage = 5; // Max 5 items per page as requested

// Helper function to transform PostTestRecord to PostTestDisplay
const transformPostTestToDisplay = (
  record: PostTestRecord,
  preTestScore?: number,
  matchingPreTest?: PreTestRecord
): PostTestDisplay => {
  const date = record.date_completed?.toDate ? record.date_completed.toDate() : new Date();

  // ✅ CRITICAL FIX: Use post_test_id as the ONLY unique identifier
  // Never generate random IDs - this causes duplicates when the same test appears twice
  if (!record.post_test_id) {
    logError("Post-test record missing post_test_id", {
      sessionId: record.session_id,
      topic: record.topic,
    });
    throw new Error("Invalid post-test record: missing document ID");
  }

  // ✅ CRITICAL FIX: Use percentage field if available, otherwise convert score to percentage
  // Post-tests now have: score (0-10) and percentage (0-100) fields
  let displayScore = 0;
  if (record.percentage !== undefined && record.percentage !== null) {
    displayScore = record.percentage; // Use percentage field (0-100)
  } else {
    const rawScore = record.score || 0;
    displayScore = rawScore <= 10 ? rawScore * 10 : rawScore; // Convert if needed
  }

  // Calculate encouraging progress message based on pre-test comparison
  let progressMessage = "";
  let progressMessageClass = "";

  if (preTestScore !== undefined && preTestScore !== null) {
    const improvement = displayScore - preTestScore;
    const maintainedHighScore = preTestScore >= 80 && displayScore >= 80;

    if (improvement >= 30) {
      progressMessage = "🚀 Huge Growth!";
      progressMessageClass = "bg-emerald-100 text-emerald-700 border border-emerald-300";
    } else if (improvement >= 15) {
      progressMessage = "📈 Great Progress!";
      progressMessageClass = "bg-green-100 text-green-700 border border-green-300";
    } else if (improvement >= 5) {
      progressMessage = "✨ Good Improvement";
      progressMessageClass = "bg-blue-100 text-blue-700 border border-blue-300";
    } else if (maintainedHighScore) {
      progressMessage = "⭐ Strong Performance";
      progressMessageClass = "bg-purple-100 text-purple-700 border border-purple-300";
    } else if (improvement >= 0) {
      progressMessage = "💪 Keep Learning";
      progressMessageClass = "bg-yellow-100 text-yellow-700 border border-yellow-300";
    } else {
      progressMessage = "🌱 Room to Grow";
      progressMessageClass = "bg-orange-100 text-orange-700 border border-orange-300";
    }
  }

  return {
    id: record.post_test_id, // ✅ Always use Firestore document ID
    topic: record.subject ? `Post-Test Assessment: ${record.subject}` : "Post-Test Assessment",
    subject: record.subject || "General",
    score: displayScore, // ✅ Now uses percentage (0-100) for display
    correctAnswers:
      record.student_answers?.filter(
        (answer, index) => answer === record.questions?.[index]?.correctAnswer
      ).length || 0,
    totalQuestions: record.questions?.length || 0,
    date_completed: date,
    session_id: record.session_id,
    testType: "post",
    preTestScore: preTestScore,
    progressMessage: progressMessage,
    progressMessageClass: progressMessageClass,
    studentNotes: matchingPreTest?.studentNotes || "",
  };
};

// Helper function to transform PreTestRecord to PostTestDisplay
const transformPreTestToDisplay = (record: PreTestRecord): PostTestDisplay => {
  // ✅ FIX: Use date_completed field (not completedAt) and add proper error handling
  let date: Date;
  if (record.date_completed?.toDate) {
    date = record.date_completed.toDate();
  } else if (record.date_completed instanceof Date) {
    date = record.date_completed;
  } else if (record.date_completed) {
    date = new Date(record.date_completed);
  } else {
    logError("Pre-test missing date_completed field", {
      sessionId: record.sessionId,
      pretestId: record.pretest_id,
    });
    date = new Date(0); // Use epoch time to indicate missing data instead of current time
  }

  if (!record.pretest_id) {
    logError("Pre-test record missing pretest_id", {
      sessionId: record.sessionId,
      topic: record.topic,
    });
    throw new Error("Invalid pre-test record: missing document ID");
  }

  return {
    id: record.pretest_id, // Use Firestore document ID
    topic: record.subject
      ? `Pre-Test Assessment: ${record.subject}`
      : record.topic || "Pre-Test Assessment",
    subject: record.subject || "General",
    score: record.percentage || 0, // Pre-tests use percentage field
    correctAnswers: record.score || 0, // Number of correct answers
    totalQuestions: record.totalQuestions || 0,
    date_completed: date,
    session_id: record.sessionId,
    testType: "pre",
    studentNotes: record.studentNotes || "",
  };
};

// Set up Firebase listeners
const setupFirebaseListeners = () => {
  logDebug("Setting up Firebase authentication listener for all tests");
  unsubscribeAuth = onAuthStateChanged(auth, (user: User | null) => {
    if (user) {
      logInfo("User authenticated, setting up test listeners");
      currentUserId.value = user.uid;
      setupPostTestsListener();
      setupPreTestsListener(); // NEW: Also listen to pre-tests
    } else {
      logInfo("User not authenticated, clearing all tests");
      currentUserId.value = null;
      postTests.value = [];
      preTests.value = [];
      isLoading.value = false;
      if (unsubscribeFirestorePostTests) {
        unsubscribeFirestorePostTests();
        unsubscribeFirestorePostTests = null;
      }
      if (unsubscribeFirestorePreTests) {
        unsubscribeFirestorePreTests();
        unsubscribeFirestorePreTests = null;
      }
    }
  });
};

const setupPostTestsListener = () => {
  if (!currentUserId.value) return;

  logDebug("Setting up Firestore post-tests listener with orderBy");

  try {
    const postTestsRef = collection(db, "post_tests");
    const postTestsQuery = query(
      postTestsRef,
      where("user_id", "==", currentUserId.value),
      orderBy("date_completed", "desc")
    );

    unsubscribeFirestorePostTests = onSnapshot(
      postTestsQuery,
      (snapshot: QuerySnapshot<DocumentData>) => {
        logDebug("Post-tests snapshot received", { count: snapshot.size });

        const tests: PostTestRecord[] = [];
        const seenIds = new Set<string>(); // ✅ FIX: Track seen IDs to prevent duplicates

        snapshot.forEach((doc) => {
          // ✅ FIX: Skip if we've already processed this document ID
          if (seenIds.has(doc.id)) {
            logWarn("Duplicate post-test document detected, skipping", { docId: doc.id });
            return;
          }

          seenIds.add(doc.id);
          const data = doc.data() as PostTestRecord;
          tests.push({
            ...data,
            post_test_id: doc.id,
          });
        });

        // ✅ FIX: Additional deduplication by document ID before transforming
        const uniqueTests = tests.filter(
          (test, index, self) =>
            index === self.findIndex((t) => t.post_test_id === test.post_test_id)
        );

        // Match each post-test with its corresponding pre-test score and notes
        postTests.value = uniqueTests.map((test) => {
          const matchingPreTest = preTestsRaw.value.find((pt) => pt.sessionId === test.session_id);
          const preTestScore = matchingPreTest?.percentage;
          return transformPostTestToDisplay(test, preTestScore, matchingPreTest);
        });

        // Only set loading to false if both test types have been attempted
        if (preTests.value.length > 0 || unsubscribeFirestorePreTests !== null) {
          isLoading.value = false;
        }
        hasError.value = false;
        logSuccess("Post-tests loaded successfully", {
          count: postTests.value.length,
          duplicatesRemoved: tests.length - uniqueTests.length,
        });
      },
      (error) => {
        // If orderBy fails due to missing index, try without it
        if (error.code === "failed-precondition") {
          logWarn("Firestore index missing for post-tests, using client-side sorting");
          setupPostTestsListenerWithoutOrder();
        } else {
          logError("Error fetching post-tests", error);
          isLoading.value = false;
          hasError.value = true;
          errorMessage.value = "Failed to load tests. Please try refreshing the page.";
        }
      }
    );
  } catch (error) {
    logError("Error setting up post-tests listener", error);
    isLoading.value = false;
    hasError.value = true;
    errorMessage.value = "Failed to connect to database. Please check your internet connection.";
  }
};

const setupPostTestsListenerWithoutOrder = () => {
  if (!currentUserId.value) return;

  logInfo("Setting up Firestore post-tests listener without orderBy (client-side sorting)");

  try {
    const postTestsRef = collection(db, "post_tests");
    const postTestsQuery = query(postTestsRef, where("user_id", "==", currentUserId.value));

    unsubscribeFirestorePostTests = onSnapshot(
      postTestsQuery,
      (snapshot: QuerySnapshot<DocumentData>) => {
        logDebug("Post-tests snapshot received (client-side sort)", { count: snapshot.size });

        const tests: PostTestRecord[] = [];
        const seenIds = new Set<string>(); // ✅ FIX: Track seen IDs to prevent duplicates

        snapshot.forEach((doc) => {
          // ✅ FIX: Skip if we've already processed this document ID
          if (seenIds.has(doc.id)) {
            logWarn("Duplicate post-test document detected (fallback), skipping", {
              docId: doc.id,
            });
            return;
          }

          seenIds.add(doc.id);
          const data = doc.data() as PostTestRecord;
          tests.push({
            ...data,
            post_test_id: doc.id,
          });
        });

        // ✅ FIX: Additional deduplication by document ID before sorting
        const uniqueTests = tests.filter(
          (test, index, self) =>
            index === self.findIndex((t) => t.post_test_id === test.post_test_id)
        );

        // Sort client-side by date_completed (most recent first)
        uniqueTests.sort((a, b) => {
          const aTime = a.date_completed?.toDate?.().getTime() || 0;
          const bTime = b.date_completed?.toDate?.().getTime() || 0;
          return bTime - aTime;
        });

        // Match each post-test with its corresponding pre-test score and notes
        postTests.value = uniqueTests.map((test) => {
          const matchingPreTest = preTestsRaw.value.find((pt) => pt.sessionId === test.session_id);
          const preTestScore = matchingPreTest?.percentage;
          return transformPostTestToDisplay(test, preTestScore, matchingPreTest);
        });

        // Only set loading to false if both test types have been attempted
        if (preTests.value.length > 0 || unsubscribeFirestorePreTests !== null) {
          isLoading.value = false;
        }
        hasError.value = false;
        logSuccess("Post-tests loaded successfully with client-side sorting", {
          count: postTests.value.length,
          duplicatesRemoved: tests.length - uniqueTests.length,
        });
      },
      (error) => {
        logError("Error fetching post-tests without orderBy", error);
        isLoading.value = false;
        hasError.value = true;
        errorMessage.value = "Failed to load tests. Please try refreshing the page.";
      }
    );
  } catch (error) {
    logError("Error setting up post-tests listener without orderBy", error);
    isLoading.value = false;
    hasError.value = true;
    errorMessage.value = "Failed to connect to database. Please check your internet connection.";
  }
};

// NEW: Setup pre-tests listener
const setupPreTestsListener = () => {
  if (!currentUserId.value) return;

  logDebug("Setting up Firestore pre-tests listener");

  try {
    const preTestsRef = collection(db, "preTests");
    // ✅ FIX: Order by date_completed field (not completedAt)
    const preTestsQuery = query(
      preTestsRef,
      where("userId", "==", currentUserId.value),
      orderBy("date_completed", "desc")
    );

    unsubscribeFirestorePreTests = onSnapshot(
      preTestsQuery,
      (snapshot: QuerySnapshot<DocumentData>) => {
        logDebug("Pre-tests snapshot received", { count: snapshot.size });

        const tests: PreTestRecord[] = [];
        const seenIds = new Set<string>();

        snapshot.forEach((doc) => {
          // ✅ FIX: Prevent duplicates
          if (seenIds.has(doc.id)) {
            logWarn("Duplicate pre-test detected, skipping", { pretestId: doc.id });
            return;
          }
          seenIds.add(doc.id);

          const data = doc.data() as Partial<PreTestRecord>;

          // ✅ CRITICAL: Log the actual date_completed value from Firebase
          logDebug("Pre-test date_completed value from Firebase", {
            pretestId: doc.id.substring(0, 8) + "***",
            date_completed_raw: data.date_completed,
            date_completed_type: typeof data.date_completed,
            hasToDate: data.date_completed?.toDate ? "yes" : "no",
          });

          const record: PreTestRecord = {
            pretest_id: doc.id,
            sessionId: data.sessionId || "",
            userId: data.userId || "",
            subject: data.subject || "General",
            topic: data.topic || "",
            date_completed: data.date_completed, // ✅ FIX: Use date_completed field
            score: data.score || 0,
            totalQuestions: data.totalQuestions || 0,
            percentage: data.percentage || 0,
            questions: data.questions || [],
            language: data.language || "en",
            sources: data.sources || [],
            studentNotes: data.studentNotes || "",
          };

          tests.push(record);
        });

        // Store raw pre-tests for matching with post-tests
        preTestsRaw.value = tests;

        // Transform to display format
        preTests.value = tests.map(transformPreTestToDisplay);

        // Log transformed dates for debugging
        preTests.value.forEach((test, index) => {
          logDebug("Transformed pre-test date", {
            index,
            pretestId: test.id.substring(0, 8) + "***",
            date_completed: test.date_completed,
            formatted: formatDate(test.date_completed),
          });
        });

        isLoading.value = false;
        hasError.value = false;
        logSuccess("Pre-tests loaded successfully", { count: tests.length });
      },
      (error) => {
        logError("Error in pre-tests listener", error);

        // Fallback to query without orderBy if index doesn't exist
        if (error.code === "failed-precondition" || error.message.includes("index")) {
          logWarn("Pre-tests orderBy failed, switching to client-side sorting");
          setupPreTestsListenerWithoutOrder();
        } else {
          logError("Pre-tests listener error", error);
          isLoading.value = false;
          hasError.value = true;
          errorMessage.value = "Failed to load pre-tests. Please try again.";
        }
      }
    );
  } catch (error) {
    logError("Error setting up pre-tests listener", error);
  }
};

// NEW: Setup pre-tests listener without orderBy
const setupPreTestsListenerWithoutOrder = () => {
  if (!currentUserId.value) return;

  logInfo("Setting up Firestore pre-tests listener without orderBy (client-side sorting)");

  try {
    const preTestsRef = collection(db, "preTests");
    // ✅ FIX: Query without orderBy (will sort client-side)
    const preTestsQuery = query(preTestsRef, where("userId", "==", currentUserId.value));

    unsubscribeFirestorePreTests = onSnapshot(
      preTestsQuery,
      (snapshot: QuerySnapshot<DocumentData>) => {
        logDebug("Pre-tests snapshot received (client-side sort)", { count: snapshot.size });

        const tests: PreTestRecord[] = [];
        const seenIds = new Set<string>();

        snapshot.forEach((doc) => {
          // ✅ FIX: Prevent duplicates
          if (seenIds.has(doc.id)) {
            logWarn("Duplicate pre-test detected (fallback), skipping", { pretestId: doc.id });
            return;
          }
          seenIds.add(doc.id);

          const data = doc.data() as Partial<PreTestRecord>;

          // ✅ CRITICAL: Log the actual date_completed value from Firebase
          logDebug("Pre-test date_completed value (fallback)", {
            pretestId: doc.id.substring(0, 8) + "***",
            date_completed_raw: data.date_completed,
            hasToDate: data.date_completed?.toDate ? "yes" : "no",
          });

          const record: PreTestRecord = {
            pretest_id: doc.id,
            sessionId: data.sessionId || "",
            userId: data.userId || "",
            subject: data.subject || "General",
            topic: data.topic || "",
            date_completed: data.date_completed, // ✅ FIX: Use date_completed field
            score: data.score || 0,
            totalQuestions: data.totalQuestions || 0,
            percentage: data.percentage || 0,
            questions: data.questions || [],
            language: data.language || "en",
            sources: data.sources || [],
            studentNotes: data.studentNotes || "",
          };

          tests.push(record);
        });

        // ✅ FIX: Sort client-side by date_completed (most recent first)
        tests.sort((a, b) => {
          const aTime =
            a.date_completed?.toDate?.().getTime() || a.date_completed?.getTime?.() || 0;
          const bTime =
            b.date_completed?.toDate?.().getTime() || b.date_completed?.getTime?.() || 0;
          return bTime - aTime;
        });

        // Store raw pre-test data for matching with post-tests
        preTestsRaw.value = tests;
        preTests.value = tests.map(transformPreTestToDisplay);
        logSuccess("Pre-tests loaded successfully with client-side sorting", {
          count: preTests.value.length,
        });
      },
      (error) => {
        logError("Error fetching pre-tests without orderBy", error);
      }
    );
  } catch (error) {
    logError("Error setting up pre-tests listener without orderBy", error);
  }
};

// Lifecycle hooks
onMounted(() => {
  logInfo("PostTestHistoryGrid mounted, initializing Firebase listeners");
  setupFirebaseListeners();
});

onUnmounted(() => {
  logInfo("PostTestHistoryGrid unmounting, cleaning up listeners");
  if (unsubscribeFirestorePostTests) {
    unsubscribeFirestorePostTests();
  }
  if (unsubscribeFirestorePreTests) {
    unsubscribeFirestorePreTests();
  }
  if (unsubscribeAuth) {
    unsubscribeAuth();
  }
});

// Computed properties for pagination (now using combined allTests)
const totalPages = computed(() => Math.ceil(allTests.value.length / itemsPerPage));

const startIndex = computed(() => (currentPage.value - 1) * itemsPerPage);
const endIndex = computed(() => Math.min(startIndex.value + itemsPerPage, allTests.value.length));

const displayedPostTests = computed(() => allTests.value.slice(startIndex.value, endIndex.value));

// Pagination functions
const goToPage = (page: number) => {
  currentPage.value = page;
};

const nextPage = () => {
  if (currentPage.value < totalPages.value) {
    currentPage.value++;
  }
};

const previousPage = () => {
  if (currentPage.value > 1) {
    currentPage.value--;
  }
};

// Format date helper
const formatDate = (date: Date | string): string => {
  try {
    const dateObj = date instanceof Date ? date : new Date(date);
    return dateObj.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "Unknown";
  }
};

// Helper function to get score badge class based on score and improvement
const getScoreBadgeClass = (test: PostTestDisplay): string => {
  // Consistent neutral purple for all tests
  return "bg-purple-50 border-purple-200";
};

// Helper function to get score text class
const getScoreTextClass = (test: PostTestDisplay): string => {
  // Consistent neutral purple for all tests
  return "text-purple-600";
};

// Handle view post-test details
const handleView = (test: PostTestDisplay) => {
  logInfo(`Opening ${test.testType}-test review modal`, {
    testId: test.id.substring(0, 8) + "***",
  });

  selectedTestId.value = test.id;
  selectedTestDetails.value = {
    topic: test.topic,
    subject: test.subject,
    score: test.score,
    passed: test.score >= 60, // Calculate based on score threshold
    date_completed: test.date_completed,
    testType: test.testType, // ✅ Pass test type to modal
  };
  isReviewModalOpen.value = true;
};

// ✅ NEW: Close modal handler
const closeReviewModal = () => {
  isReviewModalOpen.value = false;
  selectedTestId.value = null;
  selectedTestDetails.value = null;
};

// Retry loading
const retryLoading = () => {
  logInfo("User requested retry, resetting error state and restarting listeners");
  hasError.value = false;
  errorMessage.value = "";
  isLoading.value = true;
  setupFirebaseListeners();
};

const emit = defineEmits<{
  view: [test: PostTestDisplay];
}>();
</script>

<style scoped>
/* Add any additional styles if needed */
</style>
